/**
 * Design Mode — SDS SDK lazy-loading integration
 *
 * Registers with router. On activate:
 *   1. Show SDS container
 *   2. Lazy-load SDK if needed (with loading spinner)
 *   3. If product selected: init designer
 * On deactivate:
 *   1. Hide SDS container (do NOT destroy SDK instance for fast re-entry)
 *
 * Module entry — mode lifecycle + event wiring + window.SDSManager. Sibling
 * responsibilities live in: products-panel.js (catalog picker), ui-bindings.js
 * (toolbar/tabs/CTA binds), stage-views.js (loading/picker/init views),
 * preload.js (boot preload + product resolution).
 */

import { registerMode } from '../../core/router.js'
import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { createLogger } from '../../utils/logger.js'
import { loadSDK } from './sdk-loader.js'
import { initDesigner, cleanup } from './sds/sdk-init.js'
import { loadFaces } from './sds/faces.js'
import { undo, redo } from './sds/history.js'
import * as canvas from './sds/canvas.js'
import variantService from '../../services/variant-service.js'
import layerService from '../../services/layer-service.js'
import imageProcessor from '../../services/image-processor.js'
import { init as initMaterials, ensureMaterialsLoaded, selectArtsMaterial, state as materialsState } from './materials/index.js'
import { addMaterialToDesign } from './materials/add-to-design.js'
import { initPreviewModal } from './preview-modal.js'
import { handleShareBuyClick } from './share-buy/index.js'
import { saveDesignToSDS, createShopifyProductAsync } from './shopify/index.js'
import mobileModule from './mobile/index.js'
import { populateDesignProducts } from './products-panel.js'
import {
  bindToolbar,
  bindLeftTabs,
  bindCommerceButtons,
  updateProductInfoPanel,
} from './ui-bindings.js'
import { showDesignLoading, showProductPicker, startDesign } from './stage-views.js'
import { pinToPageActions } from '../page-frame/page-frame.js'

// Boot preload stays part of this module's public surface (main.js imports it).
export { preloadDesigner } from './preload.js'

const log = createLogger('design')

// H-130: design's Share & Buy CTA migrates into the unified PageHeader
// actions slot while design is active; restoreShareAction puts the node back
// in the design rail on deactivate (same node — id and listeners survive).
let restoreShareAction = null

// The manual Publish/Sync/Delete lifecycle was retired in Phase 3. Archived
// implementations live under shopify/_retired; active UI exposes only the
// canonical Share/Buy entry, which saves a DesignVersion before publishing.

/**
 * Consume a Gallery -> Design injected image (design.injectImageUrl).
 * Clears the flag immediately so it never re-triggers; runs the inject when
 * the SDK is ready or queues it on SDK_READY. Direct module call (NOT the
 * window.MaterialManager global, whose assignment timing made the old path
 * silently no-op) — addMaterialToDesign carries its own toasts.
 */
function consumePendingInject() {
  const injectImageUrl = store.getState('design.injectImageUrl')
  if (!injectImageUrl) return
  store.setState('design.injectImageUrl', null)

  const tryInject = () => {
    // Promise.resolve guards the not-ready early-return (undefined) path.
    Promise.resolve(addMaterialToDesign(injectImageUrl)).catch((err) => {
      // Errors already toasted inside; keep a log for diagnosis.
      log.warn('Gallery inject failed:', err?.message || err)
    })
  }

  if (store.getState('design.isSDKReady')) {
    tryInject()
  } else {
    eventBus.once(EVENTS.SDK_READY, tryInject)
  }
}

/**
 * H-140 design entry (?design_material= deep-link / Create "use this design"
 * exit / products-card blank preselect companion): locate + preselect the
 * entry material at the head of the Arts panel. Hung on the store as
 * design.entryMaterial (main.js boot, or the studio exit in-page); applied
 * once the SDK is ready (task-card: hang until ready) and any in-flight
 * first-page materials load has landed (so the head-insert is not overwritten
 * by the list reset). Locate/preselect ONLY — never auto-injects the canvas.
 */
function applyPendingEntryMaterial() {
  const pending = store.getState('design.entryMaterial')
  if (!pending) return
  if (!store.getState('design.isSDKReady')) {
    eventBus.once(EVENTS.SDK_READY, applyPendingEntryMaterial)
    return
  }
  if (materialsState.materials.length === 0 && materialsState.loading) {
    eventBus.once(EVENTS.MATERIALS_LOADED, applyPendingEntryMaterial)
    return
  }
  store.setState('design.entryMaterial', null)
  // selectArtsMaterial resolves a missing row by id (or synthesizes one from a
  // bare url for the Create exit's fresh, not-yet-cataloged material).
  Promise.resolve(selectArtsMaterial(pending)).catch((err) => {
    log.warn('Entry material select failed:', err?.message || err)
  })
}

/**
 * Activate Design mode
 */
async function activate() {
  log.info('Activating Design mode')
  const container = document.getElementById('sds-container')
  const placeholder = document.querySelector('#mode-design > .mode-placeholder')

  // Hide placeholder, show SDS container
  if (placeholder) placeholder.style.display = 'none'
  if (container) container.style.display = 'flex'

  // Collapse the global right panel — design brings its own right rail and
  // the 600px SDK canvas needs the width (.app.design-active in shell.css).
  document.querySelector('.app')?.classList.add('design-active')

  // Initialize mobile UI if on a mobile device
  mobileModule.init()

  // Bind toolbar actions (idempotent — checks data-bound flag)
  bindToolbar()

  // No manual stage-header buttons: publishing is automatic inside Buy Now, and
  // the Publish/Sync/Delete trio was removed (create review 2026-06-27).

  // Left tabs + side panels + commerce CTA + preview modal (idempotent binds)
  bindLeftTabs()
  bindCommerceButtons()
  initPreviewModal()
  populateDesignProducts()
  // Move the Share & Buy CTA into the PageHeader actions slot (H-130 §2).
  if (!restoreShareAction) {
    restoreShareAction = pinToPageActions(document.getElementById('btnShareBuy'), 'design')
  }
  // Materials load lazily on first Design entry (SDK_READY alone no longer
  // triggers it outside this mode). why → mobile review 2026-07-07.
  ensureMaterialsLoaded()

  const isSDKLoaded = store.getState('design.isSDKLoaded')
  const isSDKReady = store.getState('design.isSDKReady')
  const currentProduct = store.getState('design.currentProduct')

  // Process a Gallery -> Design injected image BEFORE the ready early-return:
  // with the boot preload the SDK is almost always ready, so an inject block
  // placed after that return would never run (the original silent-failure).
  consumePendingInject()
  // Same placement reasoning for the H-140 entry material (locate/preselect).
  applyPendingEntryMaterial()

  // If SDK already ready and product loaded, just show it
  if (isSDKReady && currentProduct) {
    log.info('SDK already ready, showing existing canvas')
    return
  }

  // Load SDK if not yet loaded
  if (!isSDKLoaded) {
    try {
      showDesignLoading('Loading Design SDK...')
      await loadSDK()
      log.info('SDK loaded, checking product...')
    } catch (error) {
      log.error('SDK load failed:', error)
      const designerEl = document.getElementById('designer-container')
      if (designerEl) {
        designerEl.innerHTML = `
          <div class="design-loading">
            <i class="ph ph-warning" style="font-size: 48px; color: var(--state-danger); margin-bottom: 12px;"></i>
            <div class="design-loading-text">Failed to load Design SDK</div>
            <button class="design-start-btn" onclick="location.reload()">Retry</button>
          </div>
        `
      }
      return
    }
  }

  // SDK just loaded on this entry — process any pending inject now that the
  // ready path above didn't (it queues on SDK_READY when not ready yet).
  consumePendingInject()

  // If no product selected, show picker
  if (!currentProduct) {
    showProductPicker()
    return
  }

  // SDK loaded + product selected = init designer
  await startDesign(currentProduct.id)
}

/**
 * Deactivate Design mode — hide but do NOT destroy
 */
function deactivate() {
  log.info('Deactivating Design mode (hiding, not destroying)')
  const container = document.getElementById('sds-container')
  if (container) container.style.display = 'none'

  // Return the Share & Buy CTA to the design rail (H-130).
  if (restoreShareAction) {
    restoreShareAction()
    restoreShareAction = null
  }

  // Restore the global right panel for the other modes.
  document.querySelector('.app')?.classList.remove('design-active')

  // Tear down mobile UI when leaving design mode
  mobileModule.destroy()

  // Re-show placeholder if SDK was never initialized
  if (!store.getState('design.isSDKReady')) {
    const placeholder = document.querySelector('#mode-design > .mode-placeholder')
    if (placeholder) placeholder.style.display = ''
  }
}

// Register mode with router
registerMode('design', { activate, deactivate })

// Listen for product selection events (from Products mode or right panel)
eventBus.on(EVENTS.PRODUCT_SELECT, async (product) => {
  if (!product?.id) return
  log.info('Product selected for design:', product.id)
  store.setState('design.currentProduct', product)
  updateProductInfoPanel()

  // If already in design mode, start immediately
  if (store.getState('mode') === 'design' && store.getState('design.isSDKLoaded')) {
    await startDesign(product.id)
  }
})

// Listen for SDK_READY to load faces, init materials, refresh product info
eventBus.on(EVENTS.SDK_READY, () => {
  loadFaces()
  initMaterials()
  updateProductInfoPanel()
})

// Expose SDSManager on window for backward compat
if (typeof window !== 'undefined') {
  window.SDSManager = {
    get state() {
      return {
        sdk: store.getState('design.sdkInstance'),
        isReady: store.getState('design.isSDKReady'),
        currentProductId: store.getState('design.currentProduct')?.id,
      }
    },
    initDesigner,
    cleanup,
    loadFaces,
    undo,
    redo,
    ...canvas,
    // Variant service
    selectVariantByColor: variantService.selectVariantByColor,
    selectVariantBySize: variantService.selectVariantBySize,
    loadVariants: variantService.loadVariants,
    // Layer operations
    ...layerService,
    // Image processor
    checkImageQuality: imageProcessor.checkImageQuality,
    toggleGrid: imageProcessor.toggleGrid,
    lockLayer: imageProcessor.lockLayer,
    unlockLayer: imageProcessor.unlockLayer,
    showOpacitySlider: imageProcessor.showOpacitySlider,
    // Canonical save/version/publish flow
    saveDesignToSDS,
    createShopifyProductAsync,
    handleShareBuyClick,
  }
}

log.info('Design mode registered')
